<script setup>
import { computed, useSlots } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import CustomBrandPolicyWrapper from 'dashboard/components/CustomBrandPolicyWrapper.vue';
import { getHelpUrlForFeature } from '../../../../helper/featureHelper';
import { getSettingsPageTitleIcon } from 'dashboard/helper/settingsRouteIcons';
import BackButton from '../../../../components/widgets/BackButton.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SettingsBackButton from 'dashboard/components-next/Settings/SettingsBackButton.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  titleIcon: {
    type: String,
    default: '',
  },
  linkText: {
    type: String,
    default: '',
  },
  featureName: {
    type: String,
    default: '',
  },
  backButtonLabel: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
  backUrl: {
    type: [String, Object],
    default: '',
  },
  boldTitle: {
    type: Boolean,
    default: false,
  },
  compactToolbar: {
    type: Boolean,
    default: false,
  },
});

const slots = useSlots();
const route = useRoute();
const router = useRouter();

const onInlineBackClick = () => {
  if (props.backUrl !== '' && props.backUrl != null) {
    router.push(props.backUrl);
  } else {
    router.go(-1);
  }
};

const resolvedTitleIcon = computed(() => {
  if (typeof props.titleIcon === 'string' && props.titleIcon.trim()) {
    return props.titleIcon.trim();
  }
  return getSettingsPageTitleIcon(route.name);
});

const searchQuery = defineModel('searchQuery', { type: String, default: '' });

const helpURL = getHelpUrlForFeature(props.featureName);

const showToolbar =
  Boolean(props.searchPlaceholder) ||
  Boolean(slots.actions) ||
  Boolean(slots.tabs) ||
  Boolean(slots.count);
</script>

<template>
  <div class="flex w-full min-w-0 max-w-none flex-col">
    <div class="ps-4 pe-8 md:ps-5 md:pe-10">
      <BackButton
        v-if="backButtonLabel"
        compact
        :button-label="backButtonLabel"
        class="mb-1 mt-0"
      />

      <div
        class="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="flex min-w-0 flex-1 items-start gap-1.5">
          <SettingsBackButton
            v-if="showBackButton"
            icon-only
            class="shrink-0"
            :aria-label="$t('GENERAL_SETTINGS.BACK')"
            @click="onInlineBackClick"
          />
          <div
            v-if="resolvedTitleIcon"
            class="flex shrink-0 items-center self-start"
            :class="compactToolbar ? 'min-h-6' : 'min-h-7'"
          >
            <Icon
              :icon="resolvedTitleIcon"
              class="size-[22px] shrink-0 text-n-slate-12"
            />
          </div>
          <div class="flex min-w-0 flex-1 flex-col gap-1.5">
            <h1
              v-if="title"
              class="mb-0 min-w-0 flex items-center font-sans text-lg leading-6 tracking-[-0.27px] text-n-slate-12"
              :class="[
                compactToolbar ? 'min-h-6' : 'min-h-7',
                boldTitle ? 'font-semibold' : 'font-440',
              ]"
            >
              {{ title }}
            </h1>
            <div
              v-if="description || $slots.description || linkText || helpURL"
              class="flex min-w-0 flex-col gap-1.5 text-n-slate-11"
            >
              <p
                v-if="description || $slots.description"
                class="mb-0 max-w-none text-body-main line-clamp-5 sm:line-clamp-none"
              >
                <slot name="description">{{ description }}</slot>
              </p>
              <CustomBrandPolicyWrapper
                :show-on-custom-branded-instance="false"
              >
                <a
                  v-if="helpURL && linkText"
                  :href="helpURL"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="mb-0 hidden w-fit items-center gap-1 text-sm font-medium text-n-blue-11 hover:underline sm:inline-flex"
                >
                  {{ linkText }}
                  <Icon
                    icon="i-lucide-chevron-right"
                    class="size-4 shrink-0 text-n-blue-11"
                  />
                </a>
              </CustomBrandPolicyWrapper>
            </div>
          </div>
        </div>

        <div
          v-if="$slots.headerTrailing"
          class="flex w-full shrink-0 justify-end sm:w-auto"
        >
          <slot name="headerTrailing" />
        </div>
      </div>
    </div>

    <!-- Full-width rule under title block -->
    <div
      class="w-full shrink-0 border-0 border-t border-solid border-n-weak"
      :class="compactToolbar ? 'mt-1.5' : 'mt-2'"
      role="presentation"
    />

    <!-- Toolbar: actions left; count + search right -->
    <div
      v-if="showToolbar"
      class="flex w-full flex-col ps-4 pe-8 sm:flex-row sm:items-center sm:justify-between md:ps-5 md:pe-10"
      :class="compactToolbar ? 'mt-3 gap-2 sm:gap-3' : 'mt-6 gap-3 sm:gap-4'"
    >
      <div class="flex min-w-0 flex-wrap items-center gap-3">
        <slot name="tabs" />
        <slot name="actions" />
      </div>
      <div
        class="flex w-full min-w-0 flex-wrap items-center gap-3 sm:w-auto sm:justify-end"
      >
        <slot name="count" />
        <div
          v-if="searchPlaceholder"
          class="w-full min-w-0 shrink-0 sm:w-[298px] sm:min-w-[298px] sm:max-w-[298px]"
        >
          <Input
            v-model="searchQuery"
            :placeholder="searchPlaceholder"
            :custom-input-class="
              compactToolbar
                ? '!h-[28px] !min-h-[28px] !max-h-[28px] !py-0 !px-3.5'
                : '!h-[30px] !min-h-[30px] !max-h-[30px] !py-0 !px-3.5'
            "
            class="group w-full min-w-0 gap-0 [&>input]:ltr:!pl-8 [&>input]:rtl:!pr-8 [&>input]:!rounded-[0.625rem]"
            size="sm"
            type="search"
          />
        </div>
      </div>
    </div>
  </div>
</template>
